import { useNavigate } from 'react-router-dom';
import { useBasePath, focusHref } from '../../../../lib/config/nav-helpers';
import { DataCard, ScoreRing, SkeletonList, QueryError, NotConnected, StatusBadge } from '../../../../components/ui';
import { sees } from '../../../../lib/coverage';
import { ScopeBadge } from '../../../../components/ScopeBadge';
import type { DealRiskItem } from '../../../../lib/deal-risk';

export interface DealRiskWidgetProps {
  dealRisk: { data?: any; isLoading: boolean; isError: boolean; error: unknown; refetch: () => void };
}

/**
 * Open deals most likely to slip, scored from everything on record (email,
 * calls, calendar, promises, the prospect's own words), each with its reason.
 * A written "no" is shown as lost, not "at risk".
 */
export function DealRiskWidget({ dealRisk }: DealRiskWidgetProps) {
  const navigate = useNavigate();
  const basePath = useBasePath();
  const summary = dealRisk.data?.summary;
  const deals: DealRiskItem[] = dealRisk.data?.deals ?? [];
  const lost = deals.filter((d) => d.likelyLost);
  const atRisk = deals.filter((d) => !d.likelyLost && d.riskScore >= 40);

  return (
    <DataCard
      title="Deal Risk"
      subtitle={summary ? `${summary.atRisk} at risk${summary.likelyLost ? ` · ${summary.likelyLost} said no` : ''} of ${summary.total} open` : undefined}
      accent={summary && summary.atRisk > 3 ? 'danger' : 'default'}
      badge={<ScopeBadge scope={dealRisk.data?.scope} />}
    >
      {dealRisk.isLoading ? <SkeletonList rows={2} /> : dealRisk.isError ? <QueryError error={dealRisk.error} onRetry={() => dealRisk.refetch()} compact /> : !sees(dealRisk.data?.coverage, 'crm') || !dealRisk.data?.deals ? (
        <NotConnected need="crm" />
      ) : lost.length === 0 && atRisk.length === 0 ? (
        <p className="text-2xs text-ink-dim italic">No open deal is slipping right now.</p>
      ) : (
        <div className="space-y-2">
          {[...lost.slice(0, 2), ...atRisk.slice(0, 4)].map((d) => {
            const href = d.clientId
              ? `${basePath}/clients/${d.clientId}?deal=${encodeURIComponent(d.id)}`
              : focusHref(basePath, '/pipeline', d.id);
            const why = d.headline ?? d.riskFactors[0] ?? null;
            return (
              <button type="button" key={d.id} onClick={() => navigate(href)} className="w-full text-left flex items-center justify-between gap-3 rounded-lg bg-surface-1 px-3 py-2 cursor-pointer hover:bg-surface-2 hover:shadow-lift transition-all">
                <div className="min-w-0">
                  <p className="truncate text-2xs font-medium text-ink">
                    {d.dealName}
                    {d.value > 0 ? <span className="text-ink-dim font-normal"> · ${d.value.toLocaleString()}</span> : null}
                  </p>
                  {why ? <p className="truncate text-2xs text-ink-dim" title={d.riskFactors.join(' · ')}>{why}</p> : null}
                </div>
                {d.likelyLost
                  ? <StatusBadge status="critical" label="said no" size="xs" />
                  : <ScoreRing score={d.riskScore} size={32} strokeWidth={3} colorScale="risk" />}
              </button>
            );
          })}
        </div>
      )}
    </DataCard>
  );
}
